<template>
    <div :class="classes.wrapper">
        <div :class="[classes.container, { 'has-value': hasValue }]">
            <FormLabel
                v-if="label"
                :id="id"
                :label="label"
                :required="required"
                :className="classes.label"
            />

            <FlatPickr
                :id="id"
                :name="name"
                v-model="model"
                :disabled="disabled"
                :required="required"
                :placeholder="placeholder"
                :aria-describedby="helpText ? id + '-help-block' : undefined"
                :config="currentConfig"
                :class="[classes.input, { 'has-value': hasValue}]"
            />
            <HelpText
                v-if="helpText && helpText.length > 0"
                :id="id + '-help-block'"
                :helpText="helpText"
                :className="classes.help"
            />
            <FormErrors :class="classes.errors" />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, ref, watch, onMounted} from 'vue';
import FlatPickr from 'vue-flatpickr-component';
import type { BaseOptions } from 'flatpickr/dist/types/options';
import 'flatpickr/dist/flatpickr.css';
import FormLabel from './Label.ce.vue';
import FormErrors from './Errors.ce.vue';
import HelpText from './includes/HelpText.ce.vue';

interface Classes {
    wrapper?: string;
    container?: string;
    input?: string;
    label?: string;
    help?: string;
    errors?: string;
}

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
        required: true,
    },
    modelValue: {
        type: String,
        default: '',
    },
    name: {
        type: String,
        required: true,
    },
    placeholder: {
        type: String,
        default: '',
    },
    mode: {
        type: String as () => 'single' | 'range',
        default: 'single',
    },
    helpText: {
        type: String,
        default: '',
    },
    locale: {
        type: String,
        default: 'en',
    },
    classes: {
        type: Object as () => Classes,
        default: () => ({
            wrapper: 'js-form-validation__group',
            container: 'form-floating',
            input: 'form-control form-group js-form-validation__include',
            label: 'form-label',
            help: 'form-text text-muted',
            errors: 'form-errors js-form-validation__errors invalid-feedback',
        }),
    },
    minDate: {
        type: String,
        default: undefined,
    },
    maxDate: {
        type: String,
        default: undefined,
    },
    enabledDays: {
        type: Array as () => number[],
        default: () => [],
    },
});

const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
}>();

const hasValue = ref(props.modelValue !== '' && props.modelValue != null)

const model = computed({
    get() {
        return props.modelValue;
    },
    set(value: string) {
        emit('update:modelValue', value);
        hasValue.value = value !== '' && value != null;
    },
});


const flatpickrConfig: Partial<BaseOptions> = {
    showMonths: matchMedia('(max-width: 767px)').matches ? 1 : 2,
    disableMobile: true,
    // dateFormat is the value format (modelValue). Keep ISO for submitting/binding.
    dateFormat: 'Y-m-d',
    // Show a localized, human-friendly date in the input while keeping ISO in the bound value
    altInput: true,
    inline: false,
    wrap: false,
    allowInput: false,
    static: true,
};

const localeAltFormats: Record<string, string> = {
    de: 'd.m.Y',
    en: 'm/d/Y',
    fr: 'd/m/Y',
    it: 'd/m/Y'
};

const localeImports: Record<string, () => Promise<any>> = {
    de: () => import('flatpickr/dist/l10n/de').then(m => m.German),
    fr: () => import('flatpickr/dist/l10n/fr').then(m => m.French),
    it: () => import('flatpickr/dist/l10n/it').then(m => m.Italian),
};

const loadedLocale = ref<any>(null);
const isLocaleLoading = ref(false);

async function loadLocale(locale: string) {
    if (locale === 'en' || !localeImports[locale]) {
        loadedLocale.value = null;
        return;
    }

    isLocaleLoading.value = true;
    
    try {
        loadedLocale.value = await localeImports[locale]();
    } catch (error) {
        console.error(`Failed to load locale: ${locale}`, error);
        loadedLocale.value = null;
    } finally {
        isLocaleLoading.value = false;
    }
}

onMounted(() => {
    loadLocale(props.locale);
});

watch(() => props.locale, (newLocale) => {
    loadLocale(newLocale);
});

const currentConfig = computed<Partial<BaseOptions>>(() => {
    const config: Partial<BaseOptions> = {
        ...flatpickrConfig,
        mode: props.mode === 'range' ? 'range' : 'single',
        locale:  {
            ...loadedLocale.value,
            firstDayOfWeek: 1
        },
        onChange: function(selectedDates, dateStr, instance) {
            // trigger FormValidation to re-evaluate this field
            void selectedDates;
            void dateStr;
            if(instance.altInput) {
                instance.altInput.dispatchEvent(new Event('input', {bubbles: true}));
                instance.altInput.dispatchEvent(new Event('change', {bubbles: true}));
            }
        },
        onReady: (selectedDates, dateStr, instance) => {
            void selectedDates;
            void dateStr;
            if(instance.altInput) {
                // needed for form validation
                instance.altInput.id = "datepicker_alt-" + props.id;
                instance.altInput.name = "datepicker_alt-" + props.name;
                instance.altInput.required = props.required;
            }
        },
    };

    // apply localized display format
    config.altFormat = localeAltFormats[props.locale] || localeAltFormats.en;

    if (props.minDate) {
        config.minDate = props.minDate;
    }

    if (props.maxDate) {
        config.maxDate = props.maxDate;
    }

    if (props.enabledDays && props.enabledDays.length > 0) {
        config.disable = [
            function(date: Date) {
                // flatpickr: Sunday = 0, Monday = 1, ..., Saturday = 6
                // Disable date if its day is NOT in the enabledDays array
                return !props.enabledDays.includes(date.getDay());
            }
        ];
    }

    return config;
});


</script>

